import { ArrowDown, ArrowUp, ChevronRight, ChevronsUpDown } from 'lucide-react'
import { Link, useNavigate } from '@tanstack/react-router'

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { JobLite } from '@/gen/cortado/v1/jobs_pb'
import type { OrganizationUser } from '@/gen/cortado/v1/organizations_pb'
import { JobSortFieldEnum } from '@/gen/cortado/v1/settings_pb'
import { InquiryAssigneeDropdown } from '@/components/leads'
import { PipelineInvoicePopoverCell } from '@/components/pipeline/pipeline-invoice-popover-cell'
import { PipelineEventsPopoverCell } from '@/components/pipeline/pipeline-events-popover-cell'
import {
  PIPELINE_CELL_BAR_ROW_CLASS,
  PIPELINE_CELL_INFO_CLASS,
  PIPELINE_CELL_STACK_CLASS,
  PIPELINE_CELL_TITLE_CLASS,
  PIPELINE_SEGMENT_CLASS,
  pipelineAriaSort,
  pipelineCompanyName,
  pipelineJobCustomerName,
  pipelineJobInitials,
  pipelineOwnerInitials,
  pipelinePhaseSegments,
  pipelinePhaseTitleAndInfo,
  type PipelineSort,
} from '@/components/pipeline/pipeline-job-view'

interface PipelineTableProps {
  jobs: JobLite[]
  ownersById: Map<string, OrganizationUser>
  sort: PipelineSort
  onSortChange: (field: JobSortFieldEnum) => void
}

/** Neutral avatar shared by the Customer and Owner columns — no per-name color. */
function PipelineAvatar({ initials }: { initials: string }) {
  return (
    <Avatar className="h-6 w-6 text-[10px]">
      <AvatarFallback className="bg-elevated font-semibold text-foreground select-none">{initials}</AvatarFallback>
    </Avatar>
  )
}

interface SortableHeadProps {
  label: string
  field: JobSortFieldEnum
  sort: PipelineSort
  onSortChange: (field: JobSortFieldEnum) => void
  align?: 'left' | 'right'
}

function SortableHead({ label, field, sort, onSortChange, align = 'left' }: SortableHeadProps) {
  const ariaSort = pipelineAriaSort(sort, field)
  const Icon = ariaSort === 'ascending' ? ArrowUp : ariaSort === 'descending' ? ArrowDown : ChevronsUpDown

  return (
    <TableHead aria-sort={ariaSort} className={align === 'right' ? 'text-right' : undefined}>
      <button
        type="button"
        onClick={() => onSortChange(field)}
        className={`inline-flex items-center gap-1 text-muted-foreground hover:text-foreground ${align === 'right' ? 'flex-row-reverse' : ''}`}
      >
        {label}
        <Icon size={13} />
      </button>
    </TableHead>
  )
}

export function PipelineTable({ jobs, ownersById, sort, onSortChange }: PipelineTableProps) {
  const navigate = useNavigate()

  return (
    <div className="rounded-xl border border-border bg-card text-sm">
      <Table className="table-fixed">
        <colgroup>
          <col />
          <col className="w-48" />
          <col className="w-36" />
          <col className="w-48" />
          <col className="w-44" />
          <col className="w-10" />
        </colgroup>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <SortableHead label="Customer" field={JobSortFieldEnum.JOB_SORT_FIELD_CUSTOMER_NAME} sort={sort} onSortChange={onSortChange} />
            <SortableHead label="Phase" field={JobSortFieldEnum.JOB_SORT_FIELD_BOARD_STAGE} sort={sort} onSortChange={onSortChange} />
            <SortableHead label="Events" field={JobSortFieldEnum.JOB_SORT_FIELD_FIRST_EVENT_DATE} sort={sort} onSortChange={onSortChange} />
            <SortableHead
              label="Payments"
              field={JobSortFieldEnum.JOB_SORT_FIELD_QUOTED}
              sort={sort}
              onSortChange={onSortChange}
              align="right"
            />
            <SortableHead label="Owner" field={JobSortFieldEnum.JOB_SORT_FIELD_OWNER} sort={sort} onSortChange={onSortChange} />
            <TableHead aria-hidden="true" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobs.map((job) => {
            const name = pipelineJobCustomerName(job)
            const owner = job.assignedTo ? ownersById.get(job.assignedTo) : undefined

            return (
              <TableRow
                key={job.id}
                className="cursor-pointer"
                onClick={() => navigate({ to: '/pipeline/$id', params: { id: job.id } })}
              >
                <TableCell className="py-2.5">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <PipelineAvatar initials={pipelineJobInitials(job)} />
                    <div className="min-w-0">
                      <Link
                        to="/pipeline/$id"
                        params={{ id: job.id }}
                        className="block truncate font-medium hover:underline"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {name}
                      </Link>
                      <div className="truncate text-xs text-muted-foreground">{pipelineCompanyName(job)}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="py-2.5">
                  {(() => {
                    const { title, info } = pipelinePhaseTitleAndInfo(job)
                    return (
                      <div className={PIPELINE_CELL_STACK_CLASS}>
                        <span className={PIPELINE_CELL_TITLE_CLASS}>{title}</span>
                        <div className={PIPELINE_CELL_BAR_ROW_CLASS}>
                          {pipelinePhaseSegments(job.phase).map((state, index) => (
                            <span key={index} className={`flex-1 rounded-sm ${PIPELINE_SEGMENT_CLASS[state]}`} />
                          ))}
                        </div>
                        <span className={`${PIPELINE_CELL_INFO_CLASS} text-muted-foreground`}>{info}</span>
                      </div>
                    )
                  })()}
                </TableCell>
                <TableCell className="overflow-hidden py-2.5" onClick={(event) => event.stopPropagation()}>
                  <PipelineEventsPopoverCell job={job} />
                </TableCell>
                <TableCell className="overflow-hidden py-2.5 text-right" onClick={(event) => event.stopPropagation()}>
                  <PipelineInvoicePopoverCell job={job} />
                </TableCell>
                <TableCell className="overflow-hidden py-2.5" onClick={(event) => event.stopPropagation()}>
                  {job.inquiryId ? (
                    <div className="min-w-0 overflow-hidden">
                      <InquiryAssigneeDropdown
                        inquiryId={job.inquiryId}
                        assignedTo={job.assignedTo}
                        assignedToUser={owner?.user}
                        className="h-auto max-w-full gap-1.5 rounded-md border-none bg-transparent px-1 py-0.5 text-xs font-normal shadow-none hover:bg-accent"
                      />
                    </div>
                  ) : (
                    <div className="flex min-w-0 items-center gap-1.5">
                      <PipelineAvatar initials={pipelineOwnerInitials(owner)} />
                      <span className="truncate text-xs text-muted-foreground">
                        {owner ? owner.firstName : 'Unassigned'}
                      </span>
                    </div>
                  )}
                </TableCell>
                <TableCell className="py-2.5">
                  <ChevronRight className="text-muted-foreground" size={16} />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
